import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  EvaluationInterface,
} from 'app/assessment/evaluation/models';
import { EvaluationRestConnectorService } from 'app/assessment/evaluation/services/evaluation-rest-connector.service';
import {
  AddEvaluationRecommendations,
  AddEvaluationRecommendationsCompleted,
  AddNewReviewComment,
  AddNewReviewCommentCompleted,
  DeleteReviewComment,
  DeleteReviewCommentCompleted,
  EvaluationActionType,
  LoadCurrentEvaluation,
  LoadCurrentEvaluationCompleted,
  LoadEvaluations,
  LoadEvaluationsCompleted,
  RemoveEvaluationRecommendations,
  RemoveEvaluationRecommendationsCompleted,
  UpdateCurrentEvaluation,
  UpdateCurrentEvaluationCompleted,
  UpdateEvaluation,
  UpdateEvaluationCompleted,
  UpdateEvaluationRecommendations,
  UpdateEvaluationRecommendationsCompleted,
  UpdateReviewComment,
  UpdateReviewCommentCompleted,
} from 'app/assessment/evaluation/state/actions';
import { ReviewInterface } from 'app/assessment/models/review.model';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { RecommendationInterface } from 'app/recommendation/models';
import { RecommendationRestConnectorService } from 'app/recommendation/services/recommendation-rest-connector.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable()
export class EvaluationEffects {
  public source: string = 'EvaluationEffects';

  /**
   * Load all available evaluations for a user.
   */
  @Effect()
  public loadEvaluations$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.LoadEvaluations),
      map((action: LoadEvaluations) => action.payload),
      switchMap((userId: number) => {
        return this._evaluationConnector.loadAllByUserId(userId).pipe(
          map((evaluations: EvaluationInterface[]) => new LoadEvaluationsCompleted(evaluations)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /**
   * Update the provided evaluation.
   */
  @Effect()
  public updateEvaluation$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.UpdateEvaluation),
      map((action: UpdateEvaluation) => action.payload),
      switchMap((evaluation: EvaluationInterface) => {
        return this._evaluationConnector.update(evaluation).pipe(
          map((evaluation: EvaluationInterface) => new UpdateEvaluationCompleted(evaluation)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));


  /**
   * Load new evaluation.
   */
  @Effect()
  public loadCurrentEvaluation$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.LoadCurrentEvaluation),
      map((action: LoadCurrentEvaluation) => action.payload),
      switchMap((payload: {userId: number, evaluationId: number}) => {
        return this._evaluationConnector.loadCurrentEvaluation(payload.userId, payload.evaluationId).pipe(
          map((evaluation: EvaluationInterface) => new LoadCurrentEvaluationCompleted(evaluation)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));


  /**
   * Update the current evaluation.
   */
  @Effect()
  public updateCurrentEvaluation$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.UpdateCurrentEvaluation),
      map((action: UpdateCurrentEvaluation) => action.payload),
      switchMap((evaluation: EvaluationInterface) => {
        return this._evaluationConnector.update(evaluation).pipe(
          map((evaluation: EvaluationInterface) => new UpdateCurrentEvaluationCompleted(evaluation)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));


  /**
   * Add new review to the current evaluation.
   */
  @Effect()
  public addNewReviewComment$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.AddNewReviewComment),
      map((action: AddNewReviewComment) => action.payload),
      switchMap((payload: {text: string, userId: number, evalId: number}) => {
        return this._evaluationConnector.addNewReviewComment(payload.text, payload.userId, payload.evalId).pipe(
          map((review: ReviewInterface) => new AddNewReviewCommentCompleted(review)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));


  /**
   * Update review to the current evaluation.
   */
  @Effect()
  public updateReviewComment$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.UpdateReviewComment),
      map((action: UpdateReviewComment) => action.payload),
      switchMap((payload: {review: ReviewInterface, userId: number, evalId: number}) => {
        return this._evaluationConnector.updateReviewComment(payload.review, payload.userId, payload.evalId).pipe(
          map((review: ReviewInterface) => new UpdateReviewCommentCompleted(review)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));


  /**
   * Delete review to the current evaluation.
   */
  @Effect()
  public deleteReviewComment$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.DeleteReviewComment),
      map((action: DeleteReviewComment) => action.payload),
      switchMap((payload: {review: ReviewInterface, userId: number, evalId: number}) => {
        return this._evaluationConnector.deleteReviewComment(payload.review, payload.userId, payload.evalId).pipe(
          map((review: ReviewInterface) => new DeleteReviewCommentCompleted(review)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /**
   * Update the recommendations on an evaluation.
   */
  @Effect()
  public addRecommendation$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.AddEvaluationRecommendations),
      map((action: AddEvaluationRecommendations) => action.payload),
      map((payload: { evaluation: EvaluationInterface, recommendation: RecommendationInterface }) => {
        return {
          ...payload.recommendation,
          subject: payload.evaluation.subject,
          relatedAssessmentId: payload.evaluation.id,
          relatedAssessmentType: payload.evaluation.type,
        };
      }),
      switchMap((recommendationToCreate: RecommendationInterface) => {
        return this._recommendationsConnector.addEmployeeRecommendation(recommendationToCreate).pipe(
          map((createdRecommendation: RecommendationInterface) => new AddEvaluationRecommendationsCompleted(createdRecommendation)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  @Effect()
  public updateRecommendations$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.UpdateEvaluationRecommendations),
      map((action: UpdateEvaluationRecommendations) => action.payload),
      switchMap((payload: RecommendationInterface) => {
        return this._recommendationsConnector.updateEmployeeRecommendationArray(payload).pipe(
          map((updatedRecommendation: RecommendationInterface) =>
          new UpdateEvaluationRecommendationsCompleted(updatedRecommendation)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /**
   * Update the recommendations on an evaluation.
   */
  @Effect()
  public removeRecommendations$: Observable<Action> = this.actions$
    .pipe(
      ofType(EvaluationActionType.RemoveEvaluationRecommendations),
      map((action: RemoveEvaluationRecommendations) => action.payload),
      switchMap((recommendationToDelete: RecommendationInterface) => {
        return this._recommendationsConnector.deleteEmployeeRecommendation(recommendationToDelete).pipe(
          map((removedRecommendation: RecommendationInterface) => new RemoveEvaluationRecommendationsCompleted(removedRecommendation)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  constructor(private readonly actions$: Actions,
              private readonly _evaluationConnector: EvaluationRestConnectorService,
              private readonly _recommendationsConnector: RecommendationRestConnectorService) {}

}
